import { useQuery } from "@tanstack/react-query";
import {
  GET_RELEASE_QUERY_KEY,
  getRelease,
} from "../../reader/api/readerScreenApi";
import { useEntityCache } from "../../../hooks/useEntityCache";
import { useUserEntitlementQueryKey } from "../../../hooks/useUserEntitlementQueryKey";

/**
 * Per-slot release data fetch for the suggestion feed.
 *
 * Each FeedSlot owns its own data via this hook. Critically, this does NOT
 * write to the shared readerDataStore — that store is global and gets
 * stomped/cleared whenever any reader-related component mounts/unmounts,
 * which created a swap-in race that left the feed blank between slots.
 *
 * Query key shares the GET_RELEASE_QUERY_KEY prefix with the full reader so
 * that mutations (reactions, library, comments) which invalidate that
 * prefix also invalidate the feed cache. The "freeOnly" scope keeps the
 * truncated payload in its own cache entry from the full reader's data.
 */
export function useFeedReleaseData(uuid: string, isActive: boolean) {
  const { saveEntityToCache } = useEntityCache();
  const entitlementQueryKey = useUserEntitlementQueryKey();

  const { data, isLoading, error } = useQuery({
    enabled: isActive && !!uuid,
    queryKey: [
      ...GET_RELEASE_QUERY_KEY,
      uuid,
      "freeOnly",
      ...entitlementQueryKey,
    ],
    queryFn: async () => {
      const response = await getRelease(uuid, undefined, true);
      const release = response.data.payload.results;
      // Populate the entity cache so the funnel modal (Save / Add to Library)
      // and any other consumers keyed on `${entity_type}-${id}` can find the
      // comic and artist without a separate fetch.
      if (release?.comic) {
        saveEntityToCache(release.comic);
        if (release.comic.artist) {
          saveEntityToCache(release.comic.artist);
        }
      }
      return response;
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const release = data?.data.payload.results;

  return {
    release,
    pages: release?.page_objects ?? [],
    isLoading,
    isPaywalled: release?.is_paywalled ?? false,
    totalPageCount: release?.total_page_count,
    error,
  };
}
